import React, { useEffect, useRef } from "react";
import { Tag } from "antd";
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { BenchmarkRun, BenchmarkResult } from "@/lib/api/resources/benchmark";

const isErrored = (r: BenchmarkResult): boolean =>
  !!(r.actualOutput && (r.actualOutput as any).error);
const isDone = (r: BenchmarkResult): boolean =>
  r.latencyMs != null || r.actualOutput != null;
const shortRef = (r: BenchmarkResult): string =>
  (
    r.inputReference?.split("/").pop() ||
    r.testCase?.name ||
    String(r.id).split("-")[0] ||
    "unknown"
  ).toString();

// Parse a timestamp defensively: returns null for missing/invalid values instead of an "Invalid Date".
const parseTime = (value?: string): number | null => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

const summarizeOutput = (output: any): string => {
  if (!output || typeof output !== "object") return "";
  if (output.error) return `Error: ${output.error}`;
  if (Array.isArray(output.functionCalls) && output.functionCalls.length > 0) {
    return JSON.stringify(
      output.functionCalls[0]?.arguments ?? output.functionCalls[0],
    );
  }
  if (typeof output.text === "string" && Object.keys(output).length === 1)
    return output.text;
  const values = Object.values(output);
  return values.length === 1 ? String(values[0]) : JSON.stringify(output);
};

interface RunExecutionLogProps {
  run: BenchmarkRun;
  results: BenchmarkResult[];
}

// A terminal-style, live-updating stream of per-case execution lines. Stays useful after the run
// finishes — it is the persistent log of what happened, case by case.
export const RunExecutionLog: React.FC<RunExecutionLogProps> = ({
  run,
  results,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const running = run.status === "running" || run.status === "pending";

  // Order by creation so the stream reads chronologically. Results without a usable timestamp
  // (e.g. older runs created before the column existed) keep their original order.
  const ordered = [...results].sort(
    (a, b) => (parseTime(a.createdAt) ?? 0) - (parseTime(b.createdAt) ?? 0),
  );

  useEffect(() => {
    if (running)
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [results.length, running]);

  return (
    <div className="bg-gray-950 text-gray-200 font-mono text-xs rounded-lg shadow-inner overflow-hidden border border-gray-800">
      <div className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 border-b border-gray-800">
        <span className="w-3 h-3 rounded-full bg-red-500" />
        <span className="w-3 h-3 rounded-full bg-amber-400" />
        <span className="w-3 h-3 rounded-full bg-green-500" />
        <span className="ml-3 text-gray-400 text-[11px] tracking-wide">
          benchmark · execution log
        </span>
      </div>
      <div className="h-80 overflow-y-auto p-4 space-y-1 leading-relaxed">
        {run.status === "pending" && results.length === 0 && (
          <div className="text-amber-400 animate-pulse">
            › Initializing benchmark run — preparing test cases…
          </div>
        )}
        {run.status === "running" && results.length === 0 && (
          <div className="text-amber-400 animate-pulse">
            › Spinning up agent and streaming test cases…
          </div>
        )}

        {ordered.map((r, i) => {
          const done = isDone(r);
          const errored = isErrored(r);
          const time = parseTime(r.createdAt);
          return (
            <div key={r.id} className="break-words">
              <span className="text-gray-600">
                [
                {time != null
                  ? new Date(time).toLocaleTimeString()
                  : `#${String(i + 1).padStart(2, "0")}`}
                ]
              </span>{" "}
              <span className="text-cyan-300">{shortRef(r)}</span>{" "}
              {!done ? (
                <span className="text-amber-400 animate-pulse">running…</span>
              ) : errored ? (
                <span className="text-red-400">
                  ✗ {summarizeOutput(r.actualOutput)}
                </span>
              ) : (
                <>
                  <span className="text-emerald-400">✓ done</span>
                  <span className="text-gray-400">
                    {" "}
                    {r.latencyMs != null ? `· ${r.latencyMs}ms ` : ""}
                    {r.tokensUsed != null ? `· ${r.tokensUsed} tok ` : ""}
                    {r.cost != null ? `· $${r.cost.toFixed(4)} ` : ""}
                  </span>
                  {r.qualityScore != null && (
                    <span
                      className={
                        r.qualityScore >= 8
                          ? "text-emerald-400"
                          : r.qualityScore >= 5
                            ? "text-amber-400"
                            : "text-red-400"
                      }
                    >
                      · judge {r.qualityScore}/10
                    </span>
                  )}
                  {r.correct === true && (
                    <Tag
                      color="success"
                      className="ml-2 !text-[10px] !leading-4 !px-1.5 !py-0"
                    >
                      match
                    </Tag>
                  )}
                  {r.correct === false && (
                    <Tag
                      color="warning"
                      className="ml-2 !text-[10px] !leading-4 !px-1.5 !py-0"
                    >
                      mismatch
                    </Tag>
                  )}
                </>
              )}
            </div>
          );
        })}

        {run.status === "running" && results.length > 0 && (
          <div className="text-amber-400/80 animate-pulse flex items-center gap-2 pt-1">
            <LoadingOutlined /> running benchmark cases…
          </div>
        )}
        {run.status === "completed" && (
          <div className="text-emerald-400 pt-2 border-t border-gray-800 mt-2">
            <CheckCircleOutlined /> Run completed — {run.completedCases}/
            {run.totalCases} cases
            {run.failedCases > 0 ? ` (${run.failedCases} errored)` : ""}.
          </div>
        )}
        {run.status === "failed" && (
          <div className="text-red-400 pt-2 border-t border-gray-800 mt-2">
            <CloseCircleOutlined /> Run failed
            {run.statusMessage ? `: ${run.statusMessage}` : "."}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

export default RunExecutionLog;
